import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Send, 
  Bot, 
  User, 
  Download, 
  Sparkles, 
  RefreshCcw, 
  DownloadCloud,
  ChevronDown,
  FileSpreadsheet,
  AlertCircle,
  MessageSquare,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

export default function BirdAIPage() {
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    if (activeSessionId) {
      fetchSessionHistory(activeSessionId);
    }
  }, [activeSessionId]);

  useEffect(() => {
    if (!initialLoading) {
      scrollToBottom();
    }
  }, [messages, initialLoading]);

  const fetchSessions = async () => {
    try {
      const response = await api.get('/chat/bird-ai/sessions');
      setSessions(response.data);
      if (response.data.length > 0 && !activeSessionId) {
        setActiveSessionId(response.data[0].id);
      } else if (response.data.length === 0) {
        handleNewChat();
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error('Could not load chat sessions');
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchSessionHistory = async (sessionId) => {
    try {
      const response = await api.get(`/chat/bird-ai/sessions/${sessionId}/history`);
      if (response.data.length > 0) {
        setMessages(response.data.map(m => ({
          ...m,
          timestamp: new Date(m.timestamp)
        })));
      } else {
        setMessages([
          { 
            role: 'assistant', 
            content: "Hello! I'm BirdAI. How can I help you today?",
            timestamp: new Date()
          }
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const handleNewChat = async () => {
    try {
      const response = await api.post('/chat/bird-ai/sessions', { title: 'New Chat' });
      setSessions(prev => [response.data, ...prev]);
      setActiveSessionId(response.data.id);
      setMessages([
        { 
          role: 'assistant', 
          content: "Hello! I'm BirdAI. A new session has been initialized. How can I help you?",
          timestamp: new Date()
        }
      ]);
    } catch (error) {
      console.error('Failed to create session:', error);
      toast.error('Failed to create new chat session');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading || !activeSessionId) return;

    const userMessage = { role: 'user', content: input, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.post('/chat/bird-ai', { 
        message: input,
        sessionId: activeSessionId
      });

      const aiMessage = { 
        role: 'assistant', 
        content: response.data.response, 
        reportUrl: response.data.reportUrl,
        timestamp: new Date() 
      };
      
      setMessages(prev => [...prev, aiMessage]);
      
      // Update session title in sidebar if it was the first message
      if (messages.length <= 1) {
        fetchSessions();
      }

      if (response.data.reportUrl) {
        toast.success('Fee report generated!', {
          action: {
            label: 'Download',
            onClick: () => window.open(`${process.env.REACT_APP_BACKEND_URL}${response.data.reportUrl}`, '_blank')
          }
        });
      }
    } catch (error) {
      toast.error('BirdAI connection error.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-100px)] w-full bg-white overflow-hidden">
      {/* Sidebar - Chat History */}
      <div className="w-80 border-r border-slate-100 bg-slate-50 flex flex-col">
        <div className="p-4">
          <button 
            onClick={handleNewChat}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-4 py-3 font-bold flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-indigo-100"
          >
            <Plus className="w-5 h-5" />
            New Chat
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto px-3 space-y-1 custom-scrollbar">
          <p className="px-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-4 mb-2">Conversations</p>
          {sessions.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSessionId(s.id)}
              className={`w-full text-left px-4 py-3 rounded-xl transition-all flex items-center gap-3 group ${
                activeSessionId === s.id 
                  ? 'bg-white text-indigo-600 shadow-sm border border-indigo-100' 
                  : 'text-slate-600 hover:bg-white hover:shadow-sm'
              }`}
            >
              <MessageSquare className={`w-4 h-4 ${activeSessionId === s.id ? 'text-indigo-600' : 'text-slate-400'}`} />
              <span className="text-sm font-semibold truncate flex-1">{s.title}</span>
            </button>
          ))}
        </div>

        <div className="p-4 border-t border-slate-100 bg-white">
          <div className="flex items-center gap-3 px-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 text-xs font-bold">
              AI
            </div>
            <div>
              <p className="text-xs font-bold text-slate-800">BirdAI Assistant</p>
              <p className="text-[10px] text-slate-400 font-medium">Ready to analyze</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-outfit font-bold text-base text-slate-900 leading-none mb-1">
                {sessions.find(s => s.id === activeSessionId)?.title || 'Welcome back'}
              </h2>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">Active Analysis Engine</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => activeSessionId && fetchSessionHistory(activeSessionId)}
              className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
              title="Refresh messages"
            >
              <RefreshCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-white">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`flex gap-4 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center shadow-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                <div className="space-y-1">
                  <div className={`px-5 py-3 rounded-2xl text-[13px] leading-relaxed shadow-sm ${
                    msg.role === 'user' 
                      ? 'bg-indigo-600 text-white' 
                      : msg.isError 
                        ? 'bg-red-50 text-red-700 border border-red-100'
                        : 'bg-slate-50 text-slate-800 border border-slate-100'
                  }`}>
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        table: ({node, ...props}) => (
                          <div className="overflow-x-auto my-3 rounded-xl border border-slate-200 bg-white">
                            <table className="min-w-full divide-y divide-slate-200" {...props} />
                          </div>
                        ),
                        thead: ({node, ...props}) => <thead className="bg-slate-50" {...props} />,
                        th: ({node, ...props}) => <th className="px-4 py-2 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider" {...props} />,
                        td: ({node, ...props}) => <td className="px-4 py-2 text-[12px] text-slate-600 border-t border-slate-100" {...props} />,
                        h3: ({node, ...props}) => <h3 className="font-outfit font-bold text-sm mt-4 mb-2 text-slate-900 border-b border-slate-100 pb-1" {...props} />,
                        p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                    
                    {msg.reportUrl && (
                      <div className="mt-4 pt-3 border-t border-slate-200/50">
                        <button 
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${msg.reportUrl}`, '_blank')}
                          className="bg-white hover:bg-slate-50 text-indigo-600 border border-slate-200 rounded-xl px-4 py-2 text-xs font-bold flex items-center gap-2 transition-all active:scale-95 shadow-sm"
                        >
                          <FileSpreadsheet className="w-4 h-4" />
                          Download Report
                          <DownloadCloud className="w-3 h-3 ml-1" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className={`flex items-center gap-2 px-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-tighter">
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="flex gap-4 max-w-[85%]">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center animate-pulse">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="px-4 py-2 bg-slate-50 rounded-2xl flex items-center gap-1.5 border border-slate-100">
                  <div className="w-1 h-1 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="w-1 h-1 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-1 h-1 bg-indigo-400 rounded-full animate-bounce"></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-6 bg-white border-t border-slate-100">
          <form onSubmit={handleSend} className="relative max-w-4xl mx-auto">
            <input 
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Message BirdAI..."
              className="w-full bg-slate-50 border border-slate-200 rounded-2xl pl-6 pr-14 py-4 outline-none focus:ring-4 focus:ring-indigo-100/50 focus:border-indigo-500 transition-all font-medium text-slate-800"
              disabled={loading}
            />
            <button 
              type="submit"
              disabled={!input.trim() || loading}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center hover:bg-indigo-700 disabled:bg-slate-200 transition-all active:scale-95 shadow-sm"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          <div className="mt-3 flex flex-wrap justify-center gap-4">
            {['Fee summary', 'Pending reports', 'Analyze Q4'].map((suggestion) => (
              <button 
                key={suggestion}
                onClick={() => setInput(suggestion)}
                className="text-[9px] font-bold text-slate-400 hover:text-indigo-600 uppercase tracking-widest transition-all"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #E2E8F0;
          border-radius: 10px;
        }
      `}} />
    </div>
  );
}
